// OptionStrat-style strategy classification.
//
// Maps every backend template id → {complexity, group, payoff} so the
// 5-column picker overlay can render them under the right header. Anything
// not listed here falls back to {Expert, Other}.
//
// `payoff` keys feed StrategyDiagram.jsx — the tiny SVG payoff thumbnails
// shown next to each row in the picker.

export const COMPLEXITY = ["Novice", "Intermediate", "Advanced", "Expert"];

// columnar list of group headers in the order they appear inside each
// complexity column (matches optionstrat.com/build/ visual layout)
export const GROUP_ORDER = {
  Novice: ["Basic", "Income", "Other"],
  Intermediate: ["Credit Spreads", "Debit Spreads", "Neutral", "Volatility", "Calendar Spreads", "Other"],
  Advanced: ["Directional", "Naked", "Ratio Spreads", "Synthetic", "Other"],
  Expert: ["Income", "Ladders", "Arbitrage", "Other"],
};

export const STRATEGY_META = {
  long_call:               { complexity: "Novice",       group: "Basic",            payoff: "long_call" },
  long_put:                { complexity: "Novice",       group: "Basic",            payoff: "long_put" },
  covered_call:            { complexity: "Novice",       group: "Income",           payoff: "covered_call" },
  cash_secured_put:        { complexity: "Novice",       group: "Income",           payoff: "csp" },

  bull_put_spread:         { complexity: "Intermediate", group: "Credit Spreads",   payoff: "bull_put" },
  bear_call_spread:        { complexity: "Intermediate", group: "Credit Spreads",   payoff: "bear_call" },
  bull_call_spread:        { complexity: "Intermediate", group: "Debit Spreads",    payoff: "bull_call" },
  bear_put_spread:         { complexity: "Intermediate", group: "Debit Spreads",    payoff: "bear_put" },
  iron_condor:             { complexity: "Intermediate", group: "Neutral",          payoff: "iron_condor" },
  iron_butterfly:          { complexity: "Intermediate", group: "Neutral",          payoff: "iron_butterfly" },
  call_butterfly:          { complexity: "Intermediate", group: "Neutral",          payoff: "butterfly" },
  put_butterfly:           { complexity: "Intermediate", group: "Neutral",          payoff: "butterfly" },
  long_straddle:           { complexity: "Intermediate", group: "Volatility",       payoff: "straddle" },
  long_strangle:           { complexity: "Intermediate", group: "Volatility",       payoff: "strangle" },
  calendar_call:           { complexity: "Intermediate", group: "Calendar Spreads", payoff: "calendar" },
  collar:                  { complexity: "Intermediate", group: "Other",            payoff: "collar" },

  diagonal_call:           { complexity: "Advanced",     group: "Directional",      payoff: "diagonal" },
  leaps_call:              { complexity: "Advanced",     group: "Directional",      payoff: "long_call" },
  synthetic_long:          { complexity: "Advanced",     group: "Synthetic",        payoff: "synthetic_long" },
  ratio_call_spread:       { complexity: "Advanced",     group: "Ratio Spreads",    payoff: "ratio_call" },
  ratio_put_spread:        { complexity: "Advanced",     group: "Ratio Spreads",    payoff: "ratio_put" },
  call_ratio_backspread:   { complexity: "Advanced",     group: "Ratio Spreads",    payoff: "ratio_call_back" },
  put_ratio_backspread:    { complexity: "Advanced",     group: "Ratio Spreads",    payoff: "ratio_put_back" },
  broken_wing_butterfly:   { complexity: "Advanced",     group: "Ratio Spreads",    payoff: "bwb" },

  jade_lizard:             { complexity: "Expert",       group: "Income",           payoff: "jade_lizard" },
  reverse_jade_lizard:     { complexity: "Expert",       group: "Income",           payoff: "jade_lizard" },
  zebra_call:              { complexity: "Expert",       group: "Other",            payoff: "long_call" },
  zebra_put:               { complexity: "Expert",       group: "Other",            payoff: "long_put" },
  double_diagonal:         { complexity: "Expert",       group: "Other",            payoff: "double_diagonal" },
  double_calendar:         { complexity: "Expert",       group: "Other",            payoff: "calendar" },
  box_spread:              { complexity: "Expert",       group: "Arbitrage",        payoff: "box" },
  strap:                   { complexity: "Expert",       group: "Other",            payoff: "strap" },
  strip:                   { complexity: "Expert",       group: "Other",            payoff: "strip" },
};

// Decorate a template list with classification + nice fallback. Returns a
// new array — never mutates.
export const decorateTemplates = (templates = []) =>
  templates.map((t) => {
    const meta = STRATEGY_META[t.id] || { complexity: "Expert", group: "Other", payoff: "long_call" };
    return { ...t, ...meta };
  });

// Build the {complexity: {group: [templates]}} lookup used by the picker.
export const groupByComplexity = (templates = []) => {
  const out = {};
  COMPLEXITY.forEach((c) => { out[c] = {}; });
  templates.forEach((t) => {
    const c = t.complexity || "Expert";
    const g = t.group || "Other";
    out[c] = out[c] || {};
    (out[c][g] = out[c][g] || []).push(t);
  });
  return out;
};
